import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { X, Building2, Radar, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { STATES, STATE_BY_UF, stateName } from '@/data/states';
import { useData } from '@/hooks/useData';
import { useIsVisitor, VisitorOnly } from '@/hooks/useIsVisitor';
import { CompanyAvatar, CategoryIcon, DemoBadge, RecentBadge, EmptyState } from '@/components/common/Primitives';
import { isRecent, relativeDate } from '@/lib/format';

const Kpi = ({ label, value, accent, testId }) => (
  <div className="rounded-lg border border-white/10 bg-[#0b0f16] px-3 py-3" data-testid={testId}>
    <div className={`font-display text-2xl font-semibold tabular-nums ${accent || 'text-foreground'}`}>{value}</div>
    <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
  </div>
);

export const StateSelect = ({ value, onChange, testId = 'map-state-select' }) => (
  <Select value={value || '__none'} onValueChange={(v) => onChange(v === '__none' ? null : v)}>
    <SelectTrigger className="h-10 border-white/10 bg-[#0b0f16]" data-testid={testId} aria-label="Selecionar estado">
      <SelectValue placeholder="Selecione um estado" />
    </SelectTrigger>
    <SelectContent className="max-h-72 border-white/10 bg-[#0b0f16]">
      <SelectItem value="__none">Brasil (visão nacional)</SelectItem>
      {STATES.map((s) => <SelectItem key={s.uf} value={s.uf}>{s.name} ({s.uf})</SelectItem>)}
    </SelectContent>
  </Select>
);

/** Painel do estado selecionado (ou visão nacional). */
export const StatePanel = ({ uf, onSelectUf }) => {
  const { publicCompanies, publicOpportunities, categoryById } = useData();
  const isVisitor = useIsVisitor();
  const data = useMemo(() => {
    const comps = publicCompanies.filter((c) => !uf || c.location.uf === uf);
    const opps = publicOpportunities.filter((o) => !uf || o.location.uf === uf);
    const cats = {};
    comps.forEach((c) => { cats[c.categoryId] = (cats[c.categoryId] || 0) + 1; });
    const ranking = {};
    publicCompanies.forEach((c) => { ranking[c.location.uf] = (ranking[c.location.uf] || 0) + 1; });
    return {
      comps,
      opps: [...opps].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)),
      cats: Object.entries(cats).sort((a, b) => b[1] - a[1]),
      ranking: Object.entries(ranking).sort((a, b) => b[1] - a[1]).slice(0, 5),
      recent: opps.filter((o) => isRecent(o.publishedAt)).length,
    };
  }, [uf, publicCompanies, publicOpportunities]);

  return (
    <section className="surface flex h-full flex-col p-5" data-testid="map-state-panel" aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">{uf ? `${STATE_BY_UF[uf]?.region} · ${uf}` : 'Visão nacional'}</div>
          <h3 className="mt-1 text-2xl font-semibold tracking-tight" data-testid="map-state-panel-title">{uf ? stateName(uf) : 'Brasil'}</h3>
        </div>
        {uf ? (
          <Button variant="ghost" size="icon" onClick={() => onSelectUf(null)} aria-label="Fechar painel do estado" data-testid="map-state-panel-close-button">
            <X />
          </Button>
        ) : (
          <VisitorOnly>
            <DemoBadge />
          </VisitorOnly>
        )}
      </div>
      <div className="mt-3"><StateSelect value={uf} onChange={onSelectUf} /></div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Kpi label="Empresas" value={data.comps.length} testId="state-panel-companies-count" />
        <Kpi label="Oportunidades" value={data.opps.length} testId="state-panel-opportunities-count" />
        <Kpi label="Recentes" value={data.recent} accent="text-[#ff6b84]" />
      </div>
      <VisitorOnly>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Quantidades demonstrativas · dados fictícios</p>
      </VisitorOnly>

      {!uf && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {isVisitor ? 'Estados com mais empresas de exemplo' : 'Estados com mais empresas'}
          </div>
          <ul className="space-y-1.5">
            {data.ranking.map(([u, n]) => (
              <li key={u}>
                <button type="button" onClick={() => onSelectUf(u)} className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-white/5" data-testid={`state-ranking-${u}`}>
                  <span className="w-7 font-mono text-xs text-primary">{u}</span>
                  <span className="flex-1 truncate">{stateName(u)}</span>
                  <span className="h-1 w-16 overflow-hidden rounded bg-white/5"><span className="block h-full bg-primary" style={{ width: `${(n / data.ranking[0][1]) * 100}%` }} /></span>
                  <span className="w-5 text-right font-mono text-xs text-muted-foreground">{n}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {uf && (
        <>
          <div className="mt-4">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Categorias disponíveis</div>
            {data.cats.length ? (
              <div className="flex flex-wrap gap-1.5">
                {data.cats.map(([id, n]) => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px]">
                    <CategoryIcon name={categoryById[id]?.icon} className="h-3 w-3 text-primary" /> {categoryById[id]?.name} <span className="font-mono text-muted-foreground">{n}</span>
                  </span>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Nenhuma categoria ainda.</p>}
          </div>
          <div className="mt-4 min-h-0 flex-1">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Empresas neste estado</div>
            {data.comps.length ? (
              <ul className="max-h-56 space-y-1 overflow-auto pr-1 scrollbar-thin" data-testid="state-panel-company-list">
                {data.comps.map((c) => (
                  <li key={c.id}>
                    <Link to={`/empresas/${c.id}`} className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-white/5">
                      <CompanyAvatar company={c} size={32} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{c.name}</span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" />{c.location.city}</span>
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={Building2} title="Ainda sem empresas aqui" text="Seja a primeira empresa deste estado na rede." testId="state-panel-empty" />
            )}
          </div>
        </>
      )}

      {!uf && data.opps.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">Últimas oportunidades</div>
          <ul className="space-y-1">
            {data.opps.slice(0, 3).map((o) => (
              <li key={o.id}>
                <Link to={`/oportunidades/${o.id}`} className="block rounded-md px-2 py-2 transition-colors hover:bg-white/5">
                  <span className="line-clamp-1 text-sm">{o.title}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">{isRecent(o.publishedAt) && <RecentBadge />} {o.location.city} · {o.location.uf} · {relativeDate(o.publishedAt)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-2 min-[400px]:grid-cols-2">
        <Button asChild variant="secondary" className="border border-white/10" data-testid="state-panel-explore-companies-button">
          <Link to={uf ? `/empresas?uf=${uf}` : '/empresas'}><Building2 /> Explorar empresas</Link>
        </Button>
        <Button asChild className="btn-glow" data-testid="state-panel-view-opportunities-button">
          <Link to={uf ? `/oportunidades?uf=${uf}` : '/oportunidades'}><Radar /> Ver oportunidades</Link>
        </Button>
      </div>
    </section>
  );
};